/* import __COLOCATED_TEMPLATE__ from './escalation-section.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import type EscalationRule from 'embercom/models/ai-agent/escalation-rule';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { states } from 'embercom/models/data/matching-system/matching-constants';
import type IntlService from 'ember-intl/services/intl';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { type GuidelineCategory } from 'embercom/models/ai-agent/guidelines';
import { Stat } from 'embercom/components/ai-agent/guidance/collapsible-list';

export interface Args {
  title: string;
  category: GuidelineCategory;
  description: string;
  escalationRules: EscalationRule[];
  icon: InterfaceIconName;
  maxGuidelineCount: number;
  chartDefinitionsHash: Record<string, Record<Stat, any>>;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class EscalationSection extends Component<Signature> {
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare intercomEventService: $TSFixMe;

  @tracked isOpen = true;
  @tracked sideDrawerOpen = false;
  @tracked selectedEscalationRule?: EscalationRule;
  @tracked selectedStat?: Stat;

  stats = [Stat.USED, Stat.RESOLVED, Stat.ROUTED];

  drillInColumns = [
    'conversation.id',
    'conversation.first_user_conversation_part_created_at',
    'conversation.fin.resolution_state',
    'conversation.channel_type',
    'conversation.bot_conversation_rating.rating_index',
    'conversation.fin.escalation_rules_applied.escalation_rule_ids',
  ];

  get app(): any {
    return this.appService.app;
  }

  get states(): typeof states {
    return states;
  }

  get escalationRules(): EscalationRule[] {
    return this.args.escalationRules.toArray();
  }

  get arrowIconClass(): InterfaceIconName {
    return this.isOpen ? 'small-thin-down-arrow' : 'small-thin-right-arrow';
  }

  get maybeEmptyStateClasses(): string {
    return this.escalationRules.length ? '' : 'filter grayscale opacity-50';
  }

  get headerOpenClassName(): string {
    return 'collapsible-list__header__open net-new-flag';
  }

  get openSectionId(): string | null {
    return this.aiAgentGuidelineService.openGuidelineId;
  }

  get escalationRuleCount(): number {
    return this.escalationRules.length;
  }

  get cannotAddNewEscalationRules(): boolean {
    return this.escalationRuleCount >= this.args.maxGuidelineCount || this.hasUnsavedEscalationRule;
  }

  get hasUnsavedEscalationRule(): boolean {
    return this.escalationRules.some(
      (escalationRule) =>
        escalationRule.state === states.draft && escalationRule.hasDirtyAttributes,
    );
  }

  get canAdminAccessReporting(): boolean {
    return this.permissionsService.currentAdminCan('can_access_reporting');
  }

  @action
  openSideDrawer(escalationRule: EscalationRule, stat: Stat, event: Event) {
    this.trackEvent(stat);
    event.stopPropagation();
    event.preventDefault();
    if (this.permissionsService.currentAdminCan('can_reporting__drillin__access')) {
      this.selectedEscalationRule = escalationRule;
      this.selectedStat = stat;
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  @action
  closeSideDrawer() {
    this.selectedEscalationRule = undefined;
    this.selectedStat = undefined;
  }

  @action
  trackEvent(stat_clicked: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'guidance_stat',
      place: 'guidance_card',
      section: 'guidance',
      stat: stat_clicked,
    });
  }

  @action
  toggleOpenState(): void {
    this.isOpen = !this.isOpen;
  }

  @action
  onOpenSectionChange(sectionId: string) {
    this.aiAgentGuidelineService.setOpenGuidelineId(sectionId);
  }

  @action
  createNewEscalationRule(): void {
    let predicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: [],
    });

    this.store.createRecord('ai-agent/escalation-rule', {
      name: '',
      state: states.draft,
      appId: this.app.id,
      predicateGroup,
    });
  }

  @action
  openAccordionSection(guideline: Guideline) {
    this.isOpen = true;
    this.aiAgentGuidelineService.setOpenGuidelineId(`guideline:${guideline.id}`);
  }

  @action
  async saveEscalationRule(escalationRule: EscalationRule) {
    try {
      await escalationRule.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.save-success-confirmation'),
      );
    } catch (error) {
      console.error('Failed to save escalation rule:', error);
    } finally {
      this.aiAgentGuidelineService.updatePreview();
    }
  }

  @action
  async deleteEscalationRule(escalationRule: EscalationRule) {
    try {
      await escalationRule.destroyRecord();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.delete-success-confirmation'),
      );
    } catch (error) {
      console.error('Failed to delete escalation rule:', error);
    } finally {
      this.aiAgentGuidelineService.updatePreview();
    }
  }

  @action
  async disableEscalationRule(escalationRule: EscalationRule) {
    escalationRule.state = states.draft;
    try {
      await escalationRule.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.save-success-confirmation'),
      );
    } catch (error) {
      console.error('Failed to disable escalation rule:', error);
    } finally {
      this.aiAgentGuidelineService.updatePreview();
    }
  }

  @action
  async cancelEscalationRule(escalationRule: EscalationRule) {
    escalationRule.rollbackAttributes();

    // If it's a saved escalation rule that is now empty (was just created or rolled back to empty),
    // delete it from the backend
    if (!escalationRule.isNew && !escalationRule.hasPredicates) {
      try {
        await escalationRule.destroyRecord();
      } catch (error) {
        console.error('Failed to delete empty guideline:', error);
      } finally {
        this.aiAgentGuidelineService.updatePreview();
      }
    }
  }

  @action
  async enableEscalationRule(escalationRule: EscalationRule) {
    escalationRule.state = states.live;
    await escalationRule.save();
    this.notificationsService.notifyConfirmation(
      this.intl.t('ai-agent.guidance.save-success-confirmation'),
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::EscalationSection': typeof EscalationSection;
    'ai-agent/guidance/escalation-section': typeof EscalationSection;
  }
}
